import { create } from 'zustand';

import type { PaletteName } from 'DesignSystem2/Theme/Palette/types';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ThemePreference = PaletteName | 'system' | 'accessible-system';

interface ColorPaletteOverrideActions {
    /** A temporary palette forced by a page/widget, taking priority over the user's chosen palette. */
    paletteForced: (palette: PaletteName) => void;
    paletteForceCleared: () => void;
    /** Mirrors the colorPalette bridge's resolved user preference for external consumers. */
    userChosenPreferenceResolved: (palette: PaletteName, themeMode: ThemeMode) => void;
}

interface ColorPaletteOverrideState {
    temporaryColorPalette: PaletteName | null;
    userChosenColorPalette: PaletteName;
    /** Explicit mode the user picked ('system' means "follow the OS"), as opposed to the resolved light/dark. */
    userChosenThemeMode: ThemeMode;
    actions: ColorPaletteOverrideActions;
}

export const resolveSystemPreference = (accessible: boolean): PaletteName => {
    const prefersDark =
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
            : false;
    const base: 'light' | 'dark' = prefersDark ? 'dark' : 'light';
    return accessible ? (`accessible-${base}` as PaletteName) : base;
};

// Best-effort default for the window before the colorPalette bridge attaches, so
// ForceColorMode can still resolve the correct light/dark + accessible preference. Mirrors the
// bridge's localStorage + system-preference resolution (the server-persisted-property case is
// skipped — that genuinely needs an authenticated API call). Once the bridge attaches, it overwrites
// this with the full (localStorage + server-persisted + system-preference) resolution.
const readInitialUserChosenPalette = (): PaletteName => {
    // On the server there can be a global `localStorage` that isn't a real Storage (no
    // `getItem`), so guard on the method being callable rather than on `localStorage`
    // merely existing. Access can also throw (Safari private mode), so swallow that too.
    const canUseLocalStorage = typeof window !== 'undefined' && typeof window.localStorage?.getItem === 'function';
    let stored: string | null = null;
    if (canUseLocalStorage) {
        try {
            stored = window.localStorage.getItem('kalshi-color-palette');
        } catch {
            stored = null;
        }
    }
    if (stored === 'dark' || stored === 'light' || stored === 'accessible-dark' || stored === 'accessible-light') {
        return stored;
    }
    return resolveSystemPreference(stored === 'accessible-system');
};

export const useColorPaletteOverrideStore = create<ColorPaletteOverrideState>()((set) => ({
    temporaryColorPalette: null,
    userChosenColorPalette: readInitialUserChosenPalette(),
    // The bridge overwrites this synchronously once the app effects bootstrap mounts; 'system' is
    // only observed before that runs (same caveat as `readInitialUserChosenPalette`).
    userChosenThemeMode: 'system',
    actions: {
        paletteForced: (palette) => set({ temporaryColorPalette: palette }),
        paletteForceCleared: () => set({ temporaryColorPalette: null }),
        userChosenPreferenceResolved: (palette, themeMode) =>
            set({ userChosenColorPalette: palette, userChosenThemeMode: themeMode }),
    },
}));

export const selectCurrentColorPalette = (state: ColorPaletteOverrideState): PaletteName =>
    state.temporaryColorPalette ?? state.userChosenColorPalette;

export const selectIsDark = (state: ColorPaletteOverrideState): boolean =>
    selectCurrentColorPalette(state).includes('dark');

export const selectCurrentColorPaletteLightOrDark = (state: ColorPaletteOverrideState): 'light' | 'dark' =>
    selectCurrentColorPalette(state).includes('dark') ? 'dark' : 'light';

export const selectUserChosenColorPaletteLightOrDark = (state: ColorPaletteOverrideState): 'light' | 'dark' =>
    state.userChosenColorPalette.includes('dark') ? 'dark' : 'light';

export const selectUserChosenColorPaletteRegularOrAccessible = (
    state: ColorPaletteOverrideState,
): 'regular' | 'accessible' => (state.userChosenColorPalette.includes('accessible-') ? 'accessible' : 'regular');

const buildThemePreference = (mode: ThemeMode, accessible: boolean): ThemePreference => {
    if (mode === 'system') return accessible ? 'accessible-system' : 'system';
    return accessible ? (`accessible-${mode}` as ThemePreference) : mode;
};

/**
 * Pure computation of the next stored preference for a theme-mode change. Callers (the Settings
 * page) still own persisting the result by writing `colorPalette` through the userProperties
 * mutation — that cache write is what the colorPalette bridge reacts to, closing the loop back
 * into this store.
 */
export const nextThemeModePreference = (state: ColorPaletteOverrideState, mode: ThemeMode): ThemePreference =>
    buildThemePreference(mode, selectUserChosenColorPaletteRegularOrAccessible(state) === 'accessible');

/** Pure computation of the next stored preference for an accessible-colors toggle. */
export const nextAccessiblePreference = (state: ColorPaletteOverrideState, accessible: boolean): ThemePreference =>
    buildThemePreference(state.userChosenThemeMode, accessible);
